import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Notifications.css";
import SalonService from "../../services/SalonService";

export default function Notifications() {
    const customerId = localStorage.getItem("CUSTOMER_ID");
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // 🔹 Ref for the last notification element
    const observer = useRef();

    useEffect(() => {
        fetchNotifications(page);
        // eslint-disable-next-line
    }, [page]);

    const fetchNotifications = async (pageNumber) => {
        if (!hasMore) return;

        setLoading(true);
        try {
            const response = await SalonService.getNotifications(customerId, pageNumber, 10);
            const newItems = response.data.content || [];

            if (pageNumber === 0) {
                setNotifications(newItems);
            } else {
                setNotifications((prev) => [...prev, ...newItems]); // append
            }

            setHasMore(!response.data.last); // check if more pages available
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Intersection Observer callback
    const lastNotificationRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    setPage((prev) => prev + 1); // load next page
                }
            });

            if (node) observer.current.observe(node);
        },
        [loading, hasMore]
    );

    const markAsRead = async (id) => {
        try {
            await SalonService.updateNotification(id);
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, read: true } : n))
            );
        } catch (error) {
            console.error("Error updating notification:", error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await Promise.all(
                notifications.filter((n) => !n.read).map((n) => SalonService.updateNotification(n.id))
            );
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        } catch (error) {
            console.error("Error marking all as read:", error);
        }
    };

    return (
        <div className="notifications-page">
            <div className="notifications-header">
                <h2>Notifications</h2>
                {notifications.some((n) => !n.read) && (
                    <button className="mark-all-btn" onClick={markAllAsRead}>
                        Mark all as read
                    </button>
                )}
            </div>

            {notifications.length === 0 && !loading ? (
                <p className="empty-msg">No notifications</p>
            ) : (
                <div className="notifications-list">
                    {notifications.map((n, index) => {
                        if (index === notifications.length - 1) {
                            // attach ref to last item
                            return (
                                <div
                                    ref={lastNotificationRef}   // ✅ add this
                                    key={n.id}
                                    className={`notification-card ${n.read ? "read" : "unread"}`}
                                    onClick={() => markAsRead(n.id)}
                                >
                                    <h4>{n.title}</h4>
                                    <p>{n.message}</p>
                                    <small>{new Date(n.createdAt).toLocaleString("en-GB")}</small>
                                </div>
                            );
                        } else {
                            return (
                                <div
                                    key={n.id}
                                    className={`notification-card ${n.read ? "read" : "unread"}`}
                                    onClick={() => markAsRead(n.id)}
                                >
                                    <h4>{n.title}</h4>
                                    <p>{n.message}</p>
                                    <small>{new Date(n.createdAt).toLocaleString("en-GB")}</small>
                                </div>
                            );
                        }
                    })}

                </div>
            )}

            {loading && <p style={{ textAlign: "center", marginTop: "10px" }}>Loading...</p>}
        </div>
    );
}
